import { useEffect, useMemo, useRef, useState } from 'react';
import io, { Socket } from 'socket.io-client';
import { getSocketUrl, getSocketConfig } from '@easyflow/ui-frontend';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import { AUTH_STORAGE_KEYS } from '@/constants/auth';
import type {
  ClusterHealthStrip,
  AnomalyItem,
  CarrierKpis,
  PerTenantKpiRow,
  TenantFsHeatmapCell,
  TenantFsMapRow,
  HashSkewEntry,
  KamailioDispatcherDestination,
  TrunkFailoverEvent,
  KamailioTimeSeries,
  FsNodeTimeSeries,
  SipResponseMatrixCell,
  RtpWorstCall,
  RegSecuritySnapshot,
  KamailioSyncHealth,
  NocActionLogEntry,
  TimeSeriesWindow,
  ErrorTrackingIssueBroadcast,
  ClusterEvent,
} from '@easyflow/shared/types/api';
import type { NocAorSnapshot, NocHtableSnapshot } from '@easyflow/shared/types/noc-runtime';

export interface ClusterRealtimeState {
  health: ClusterHealthStrip | null;
  anomalies: AnomalyItem[];
  events: ClusterEvent[];
  socketConnected: boolean;
}

const MAX_EVENT_BUFFER = 500;

export function useClusterRealtime(): ClusterRealtimeState {
  const [health, setHealth] = useState<ClusterHealthStrip | null>(null);
  const [anomalies, setAnomalies] = useState<AnomalyItem[]>([]);
  const [events, setEvents] = useState<ClusterEvent[]>([]);
  const [socketConnected, setSocketConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    const token = typeof window !== 'undefined' ? localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN) : null;
    if (!token) return;

    // Task #2569 — config Socket.IO condivisa (riconnessione infinita + jitter).
    const socket = io(getSocketUrl(), {
      ...getSocketConfig(),
      auth: { token },
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      setSocketConnected(true);
      // Il delegatore onAny del backend mappa `subscribe:<room>` → joinRoom(<room>):
      // la room va nel NOME dell'evento, non come payload (Task #2675).
      socket.emit('subscribe:master:sip-cluster');
    });
    socket.on('disconnect', () => setSocketConnected(false));

    socket.on('cluster:health', (p: ClusterHealthStrip) => setHealth(p));
    socket.on('cluster:anomalies', (p: AnomalyItem[]) => setAnomalies(p));
    socket.on('cluster:event', (e: ClusterEvent) => {
      setEvents((prev) => [e, ...prev].slice(0, MAX_EVENT_BUFFER));
    });

    return () => {
      socket.emit('unsubscribe:master:sip-cluster');
      socket.disconnect();
      socketRef.current = null;
    };
  }, []);

  return { health, anomalies, events, socketConnected };
}

const MAX_ERROR_TRACKING_BUFFER = 100;

export interface ErrorTrackingFeedState {
  issues: ErrorTrackingIssueBroadcast[];
  socketConnected: boolean;
}

/**
 * Task #2604 — Feed live delle issue error-tracking (GlitchTip) ricevute via
 * webhook backend e ribroadcastate sulla room `master:sip-cluster`
 * (`noc:error-tracking:issue`). Buffer in-memory volatile (no persistenza FE).
 */
export function useErrorTrackingIssuesFeed(): ErrorTrackingFeedState {
  const [issues, setIssues] = useState<ErrorTrackingIssueBroadcast[]>([]);
  const [socketConnected, setSocketConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    const token = typeof window !== 'undefined' ? localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN) : null;
    if (!token) return;

    const socket = io(getSocketUrl(), {
      ...getSocketConfig(),
      auth: { token },
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      setSocketConnected(true);
      socket.emit('subscribe:master:sip-cluster');
    });
    socket.on('disconnect', () => setSocketConnected(false));
    socket.on('noc:error-tracking:issue', (p: ErrorTrackingIssueBroadcast) => {
      setIssues((prev) => [p, ...prev].slice(0, MAX_ERROR_TRACKING_BUFFER));
    });

    return () => {
      socket.emit('unsubscribe:master:sip-cluster');
      socket.disconnect();
      socketRef.current = null;
    };
  }, []);

  return { issues, socketConnected };
}

interface RestOptions {
  refetchIntervalMs?: number;
  enabled?: boolean;
}

function useNocQuery<T>(key: string, url: string, opts: RestOptions = {}) {
  return useQuery<T>({
    queryKey: ['noc', key, url],
    queryFn: async () => {
      const res = await apiClient.get<T>(url);
      if (!res.success || res.data === undefined) {
        throw new Error(res.error ?? `NOC query failed: ${url}`);
      }
      return res.data;
    },
    refetchInterval: opts.refetchIntervalMs ?? 15_000,
    enabled: opts.enabled ?? true,
    staleTime: 5_000,
  });
}

export function useHealthStrip(opts?: RestOptions) {
  return useNocQuery<ClusterHealthStrip>('health', '/master/voip-cluster/health', opts);
}
export function useAnomalies(opts?: RestOptions) {
  return useNocQuery<AnomalyItem[]>('anomalies', '/master/voip-cluster/anomalies', opts);
}
export function useCarrierKpis(scope: 'cluster' | 'fs' | 'tenant' = 'cluster', opts?: RestOptions) {
  return useNocQuery<CarrierKpis>(`carrier-${scope}`, `/master/voip-cluster/kpis/carrier?scope=${scope}`, opts);
}
export function usePerTenantKpis(opts?: RestOptions) {
  return useNocQuery<PerTenantKpiRow[]>('per-tenant', '/master/voip-cluster/kpis/per-tenant', opts);
}
export function useTenantFsHeatmap(opts?: RestOptions) {
  return useNocQuery<TenantFsHeatmapCell[]>('tenant-fs-heatmap', '/master/voip-cluster/tenants/fs-heatmap', opts);
}
export function useTenantFsMap(opts?: RestOptions) {
  return useNocQuery<TenantFsMapRow[]>('tenant-fs-map', '/master/voip-cluster/tenants/fs-map', opts);
}
export function useHashSkew(opts?: RestOptions) {
  return useNocQuery<HashSkewEntry[]>('hash-skew', '/master/voip-cluster/dispatcher/hash-skew', opts);
}
export function useDispatcherState(opts?: RestOptions) {
  return useNocQuery<KamailioDispatcherDestination[]>('dispatcher', '/master/voip-cluster/dispatcher/state', opts);
}
export function useTrunkFailovers(hours: number = 24, opts?: RestOptions) {
  return useNocQuery<TrunkFailoverEvent[]>(`failovers-${hours}`, `/master/voip-cluster/trunks/failovers?hours=${hours}`, opts);
}
export function useKamailioTimeSeries(window: TimeSeriesWindow = '15m', opts?: RestOptions) {
  return useNocQuery<KamailioTimeSeries>(`kam-ts-${window}`, `/master/voip-cluster/kamailio/timeseries?window=${window}`, opts);
}
export function useFsTimeSeries(nodeId: string, window: TimeSeriesWindow = '15m', opts?: RestOptions) {
  return useNocQuery<FsNodeTimeSeries>(`fs-ts-${nodeId}-${window}`, `/master/voip-cluster/fs/${nodeId}/timeseries?window=${window}`, { ...opts, enabled: !!nodeId });
}
export function useSipResponseMatrix(opts?: RestOptions) {
  return useNocQuery<SipResponseMatrixCell[]>('sip-matrix', '/master/voip-cluster/signaling/response-matrix', opts);
}
export function useRtpWorstCalls(opts?: RestOptions) {
  return useNocQuery<RtpWorstCall[]>('rtp-worst', '/master/voip-cluster/media/rtp-worst', opts);
}
export function useRtpMosDistribution(opts?: RestOptions) {
  return useNocQuery<{ mosValues: number[] }>('rtp-mos', '/master/voip-cluster/media/mos-distribution', opts);
}
export function useRegSecurity(opts?: RestOptions) {
  return useNocQuery<RegSecuritySnapshot>('reg-sec', '/master/voip-cluster/security/registrations', opts);
}
export function useKamailioSyncHealth(opts?: RestOptions) {
  return useNocQuery<KamailioSyncHealth>('kam-sync', '/master/voip-cluster/kamailio/sync-health', opts);
}
export function useNocAuditLog(limit: number = 100, opts?: RestOptions) {
  return useNocQuery<NocActionLogEntry[]>(`audit-${limit}`, `/master/voip-cluster/ops/audit?limit=${limit}`, opts);
}

// Task #2176 — Runtime inspectors per pannelli NOC Round B2.
// `enabled` su useNocRuntimeAor consente di accendere il polling solo quando il
// tab Extensions Live è visibile (risparmio richieste RPC sui nodi KAM).
export function useNocRuntimeAor(opts?: RestOptions) {
  return useNocQuery<NocAorSnapshot>('runtime-aor', '/master/voip-cluster/runtime/aor', {
    refetchIntervalMs: 10_000,
    ...opts,
  });
}
export function useNocRuntimeHtable(name: string, opts?: RestOptions) {
  return useNocQuery<NocHtableSnapshot>(
    `runtime-htable-${name}`,
    `/master/voip-cluster/runtime/htable/${encodeURIComponent(name)}`,
    { refetchIntervalMs: 30_000, ...opts, enabled: (opts?.enabled ?? true) && name.length > 0 },
  );
}

interface FsNodeDescriptor { nodeId: string; enabled: boolean; drain: boolean }
interface KamailioNodeProbe { nodeId: string }

export function useFsNodeIds(): string[] {
  const { data } = useNocQuery<FsNodeDescriptor[]>('fs-nodes', '/master/voip-cluster/fs/nodes', { refetchIntervalMs: 60_000 });
  return useMemo(() => (data ?? []).filter((n) => n.enabled).map((n) => n.nodeId), [data]);
}
export function useKamailioNodeIds(): string[] {
  const { data } = useNocQuery<KamailioNodeProbe[]>('kam-nodes', '/master/voip-cluster/kamailio/nodes', { refetchIntervalMs: 60_000 });
  return useMemo(() => (data ?? []).map((n) => n.nodeId), [data]);
}
